import { Dispatch } from 'redux';
import { createHandler } from '@mews/std';
import {
    beforeSendHook,
    BrowserOptions,
    browserTracingIntegration,
    initSentry,
    LogFunction,
    replayIntegration,
    SentryReporterService,
} from '../../services';
import { ERROR_LOGGED, SentryActions, TAGS_SET, USER_SET } from './actions';
import { WARNING_LOGGED } from './actions/logSentryWarning';

interface CreateSentryMiddlewareProps extends BrowserOptions {
    sentryReporterService: SentryReporterService;
    shouldRecordErrorSessions?: boolean;
    shouldInstrumentRoutingEventsAutomatically?: boolean;
}

const createLogHandler = (sentryReporterService: SentryReporterService) =>
    createHandler<typeof WARNING_LOGGED | typeof ERROR_LOGGED, LogFunction>(
        {
            [WARNING_LOGGED]: sentryReporterService.logWarning,
            [ERROR_LOGGED]: sentryReporterService.logError,
        },
        sentryReporterService.logError
    );

export const createSentryMiddleware = ({
    release,
    environment,
    sentryReporterService,
    shouldRecordErrorSessions,
    tracesSampler,
    shouldInstrumentRoutingEventsAutomatically = true,
    ...sentryBrowserOptions
}: CreateSentryMiddlewareProps) => {
    const extraIntegrations = [];

    if (shouldRecordErrorSessions === true) {
        extraIntegrations.push(replayIntegration());
    }

    initSentry({
        ...sentryBrowserOptions,
        beforeSend: beforeSendHook,
        environment,
        release,
        normalizeDepth: 4,
        integrations: [
            browserTracingIntegration({
                instrumentNavigation: shouldInstrumentRoutingEventsAutomatically,
                instrumentPageLoad: shouldInstrumentRoutingEventsAutomatically,
            }),
            ...extraIntegrations,
        ],
        tracesSampler,
        tracePropagationTargets: [/^\/api/],
        ...(shouldRecordErrorSessions === true
            ? {
                  replaysSessionSampleRate: 0,
                  replaysOnErrorSampleRate: 0.5,
              }
            : {}),
    });

    const logHandler = createLogHandler(sentryReporterService);

    const { addBreadcrumb, setTags, setUser } = sentryReporterService;

    return () => (next: Dispatch) => (action: SentryActions) => {
        const { type } = action;

        if (action.type === ERROR_LOGGED || action.type === WARNING_LOGGED) {
            const { error, extra } = action.payload;
            const data = { error, extra };
            logHandler(action.type, data);
        }

        if (action.type === TAGS_SET) {
            setTags(action.payload);
        }

        if (action.type === USER_SET) {
            setUser(action.payload);
        }

        if (type && !type.startsWith('@')) {
            addBreadcrumb({ message: type });
        }

        next(action);
    };
};
